// ── Add-to-playlist opener seam (injection, not import) ───────────────────────
//
// `AddToPlaylistModal` lives in `@/library` and can't move down: it depends on
// `@/library/playlistPicker`, `@/library/CreatePlaylistModal`, and
// `@/playlistdetail/playlistDetailService`. Feature dirs that open it (track,
// podcast, album) must NOT import `@/library` directly — that back-edge closes
// the `library ↔ track` / `library ↔ podcast` biref cycles the ratchet rejects.
// Instead callers invoke the neutral `openAddToPlaylist`
// here, and library REGISTERS its real implementation at boot from a node with
// no inbound edges (`app.ts`). The dependency flows one way; this module never
// imports library.

/** Snapshot of what to add, captured at open time so a recycled/refreshed row
 * still targets the intended track(s). */
export interface AddToPlaylistTarget {
    /** Single track/episode asin to add. */
    readonly trackId?: string | null
    /** Title of `trackId`, named in the duplicate-song confirmation. */
    readonly trackTitle?: string | null
    /** Multi-track add (album). Takes precedence over `trackId` when non-empty. */
    readonly trackIds?: readonly string[] | null
}

/** Reports the `addToPlaylistOverflow` click, tagged with the sign-in fields when the press was gated. */
export type AddToPlaylistClickReporter = (signInMetrics: { readonly actionDetail?: string }) => void

export type AddToPlaylistOpener = (
    target: AddToPlaylistTarget,
    reportClick?: AddToPlaylistClickReporter
) => void

let opener: AddToPlaylistOpener | null = null

/**
 * Register the library-owned modal opener. Idempotent (a re-register replaces
 * the impl). Called at boot from the non-library bootstrap so no
 * `library → caller` import edge is created.
 */
export function registerAddToPlaylistOpener(impl: AddToPlaylistOpener): void {
    opener = impl
}

/**
 * Open the Add to Playlist modal for `target`; the registered impl prompts
 * sign-in instead for an anonymous session, handing `reportClick` the sign-in
 * click fields. A no-op until library has registered its impl (boot registers
 * eagerly, so this only bites in a headless render or an unwired test).
 */
export const openAddToPlaylist: AddToPlaylistOpener = (target, reportClick) => {
    opener?.(target, reportClick)
}
